import React, { PropsWithChildren, ReactElement, useEffect } from 'react'
import ShowAlertOnModalOrTrayWrapper from 'components/Dashboard/CommonComponents/ShowAlertOnModalOrTrayWrapper'
import { createPortal } from 'react-dom'
import ConfirmDeletionContent, { ConfirmDeletionContentProps } from './ConfirmDeletionContent'
import ModalDialog from 'ui/NewModalDialog'
import { Flex } from '@theme-ui/components'
import { setIsModalDialogVisible } from 'store/dashboard/dashboard'
import { useAppDispatch } from 'store/hooks'
import NotificationIconAndMessage from './DialogContentElements/NotificationIconAndMessage'
import LastQuery from './DialogContentElements/LastQuery'

interface ConfirmDeletionModalDialogProps extends ConfirmDeletionContentProps {
  element: Element | null
  title: string
  testId?: string
  timeSinceLastQuery?: string
  notificationText: string
  onClose: () => void
  shouldAddShowAlertOnModalWrapper?: boolean
}

export default function ConfirmDeletionModalDialog({
  element,
  title,
  testId,
  onClose,
  type,
  timeSinceLastQuery,
  notificationText,
  checkboxText,
  isRequestInFlight,
  shouldShowPassword,
  onDelete,
  onCancel,
  children,
  shouldAddShowAlertOnModalWrapper = true,
}: PropsWithChildren<ConfirmDeletionModalDialogProps>): ReactElement | null {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setIsModalDialogVisible(true))

    return () => {
      dispatch(setIsModalDialogVisible(false))
    }
  }, [dispatch])

  if (!element) {
    return <></>
  }

  return createPortal(
    <ConfirmDeletionModalWrapper
      shouldAddShowAlertOnModalWrapper={shouldAddShowAlertOnModalWrapper}
    >
      <ModalDialog
        shouldCreatePortal={!shouldAddShowAlertOnModalWrapper || false}
        isOpen
        title={title}
        dataTestId={testId}
        onCloseClick={onClose}
        dismiss={onClose}
        footer={
          <Flex
            key={title}
            sx={{
              minHeight: '3.2rem',
            }}
          />
        }
      >
        {children || (
          <ConfirmDeletionContent
            type={type}
            isRequestInFlight={isRequestInFlight}
            checkboxText={checkboxText}
            shouldShowPassword={shouldShowPassword}
            onCancel={onCancel}
            onDelete={onDelete}
          >
            <NotificationIconAndMessage message={notificationText} />
            {timeSinceLastQuery && <LastQuery timeSinceLastQuery={timeSinceLastQuery} />}
          </ConfirmDeletionContent>
        )}
      </ModalDialog>
    </ConfirmDeletionModalWrapper>,
    element,
  )
}

function ConfirmDeletionModalWrapper({
  shouldAddShowAlertOnModalWrapper,
  children,
}: PropsWithChildren<{ shouldAddShowAlertOnModalWrapper: boolean }>) {
  if (shouldAddShowAlertOnModalWrapper) {
    return (
      <ShowAlertOnModalOrTrayWrapper shouldShowAlertOnModal>
        {children}
      </ShowAlertOnModalOrTrayWrapper>
    )
  }

  return <>{children}</>
}
